import {useCallback} from "react";
import {useQueryClient} from "@tanstack/react-query";
import type {Method} from "axios";
import {useCustomQuery} from "~/query/queryClient";
import {queryHttp} from "~/query/queryHttp";
import {recordUsersUrl, recordUrl} from "../services/categoryProfileUrls";
import type {CategoryRecord, RecordWriteResponse} from "../types/categoryRecordTypes";

/** The authored record of one category. An empty record (`exists: false`) until somebody writes. */
export type AppUser = {id: number; name: string; email: string};

/** App users who can open the dataset (for picking the category owner and team members). */
export function useAppUsersQuery(databagId: number) {
    const url = recordUsersUrl(databagId);
    return useCustomQuery({
        queryKey: [url] as const,
        queryFn: async () => (await queryHttp.get<AppUser[]>(url)).data,
        enabled: databagId > 0,
        staleTime: 5 * 60_000,
        retry: false,
    });
}

export function useCategoryRecordQuery(databagId: number, path: string) {
    const url = recordUrl(databagId, path);
    return useCustomQuery({
        queryKey: [url] as const,
        queryFn: async () => (await queryHttp.get<RecordWriteResponse>(url)).data.record,
        enabled: databagId > 0 && Boolean(path),
        retry: false,
    });
}

/** Sends one write and puts the record from the answer in the cache, so every section updates at once. */
export function useRecordWrite(databagId: number, path: string) {
    const queryClient = useQueryClient();
    return useCallback(async (method: Method, url: string, data?: unknown): Promise<RecordWriteResponse> => {
        // queryHttp defaults to JSON, which makes axios turn FormData into JSON and drop the file. Multipart instead;
        // the browser then sets the boundary.
        const headers = data instanceof FormData ? {'Content-Type': 'multipart/form-data'} : undefined;
        const response = await queryHttp.request<RecordWriteResponse>({method, url, data, headers});
        queryClient.setQueryData<CategoryRecord>([recordUrl(databagId, path)], response.data.record);
        return response.data;
    }, [queryClient, databagId, path]);
}
